import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { RotateCcw, UserCircle, UserPlus, UserX, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { HiringProcessInstanceProgress } from "@/features/hiring-process/instance-progress";
import { RevealProviderButton } from "@/features/provider/reveal-provider-button";
import { useOrganization } from "@/hooks/use-organization";
import { useOrgPath } from "@/hooks/useOrgPath";
import type { JobPostDecisionReason } from "@api/shared-runtime/enums";
import { WITHDRAWAL_REASON_LABELS } from "../../withdrawal-reasons";
import { CandidateNotesThread } from "../candidate-notes-thread";
import {
  CandidateStatusBadge,
  LapsedRequirementBadges,
} from "./candidate-badges";
import {
  getCandidateDisplayName,
  formatCandidateSubtitle,
  getCandidateFlags,
  getRecentEmployers,
  type Candidate,
  type CandidatePermissions,
  type LapsedRequirement,
} from "./candidate-views";
import type { JobDashboardActions } from "./use-job-dashboard-actions";

function SheetSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="min-w-0 space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h3>
      {children}
    </section>
  );
}

function ActionButton({
  icon: Icon,
  onClick,
  disabled,
  children,
}: {
  icon: typeof UserCircle;
  onClick: () => void;
  disabled?: boolean;
  children: string;
}) {
  return (
    <Button
      variant="outline"
      className="justify-start gap-2"
      onClick={onClick}
      disabled={disabled}
    >
      <Icon aria-hidden="true" size={16} />
      {children}
    </Button>
  );
}

function ProviderPools({
  candidate,
  displayName,
  canRemove,
  actions,
}: {
  candidate: Candidate;
  displayName: string;
  canRemove: boolean;
  actions: JobDashboardActions;
}) {
  const orgPath = useOrgPath();
  const pools = candidate.providerPools ?? [];
  if (pools.length === 0) {
    return null;
  }
  return (
    <SheetSection title="Provider pools">
      <div className="flex flex-wrap items-center gap-1.5">
        {pools.map((pool) => (
          <span
            key={pool.id}
            className="inline-flex max-w-full items-center gap-1 rounded-xl border border-slate-200 bg-slate-100 py-0.5 pl-3 pr-1 text-sm text-slate-700"
          >
            <Link
              to={orgPath("/providers/pools/" + pool.id)}
              className="min-w-0 py-1 [overflow-wrap:anywhere] hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
            >
              {pool.name}
            </Link>
            {canRemove && (
              <button
                type="button"
                aria-label={`Remove from ${pool.name}`}
                className="ml-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg hover:bg-slate-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
                onClick={() =>
                  actions.openRemoveFromPool({
                    providerPoolId: pool.id,
                    poolName: pool.name,
                    userId: candidate.userId,
                    candidateName: displayName,
                  })
                }
              >
                <X aria-hidden="true" size={14} />
              </button>
            )}
          </span>
        ))}
      </div>
    </SheetSection>
  );
}

// Withdrawal is grouped into its own panel so the status, reasons, and note
// read as one section separate from the candidate's identity.
function WithdrawalPanel({ candidate }: { candidate: Candidate }) {
  const reasonLabels = candidate.withdrawalReasons
    .map((r) => WITHDRAWAL_REASON_LABELS[r.reason as JobPostDecisionReason])
    .filter(Boolean);

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex min-w-0 items-center gap-1.5 [overflow-wrap:anywhere]">
        <UserX
          aria-hidden="true"
          size={14}
          className="shrink-0 text-slate-500"
        />
        <span className="text-sm font-semibold text-slate-700">
          Withdrew their interest
        </span>
        {candidate.withdrawnAt && (
          <span className="text-xs text-slate-500">
            {format(new Date(candidate.withdrawnAt), "MMM d, yyyy")}
          </span>
        )}
      </div>
      {reasonLabels.length > 0 && (
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Reasons
          </span>
          <span className="flex flex-wrap items-center gap-1.5">
            {reasonLabels.map((label) => (
              <Badge
                key={label}
                variant="outline"
                className="max-w-full whitespace-normal border-slate-200 bg-white text-xs text-slate-600"
              >
                {label}
              </Badge>
            ))}
          </span>
        </div>
      )}
      {candidate.withdrawalNote && (
        <div className="flex min-w-0 flex-wrap items-baseline gap-2">
          <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Note
          </span>
          <p className="min-w-0 text-sm italic text-slate-600 [overflow-wrap:anywhere]">
            “{candidate.withdrawalNote}”
          </p>
        </div>
      )}
    </div>
  );
}

function SheetActions({
  candidate,
  permissions,
  actions,
}: {
  candidate: Candidate;
  permissions: CandidatePermissions;
  actions: JobDashboardActions;
}) {
  const { selectedOrgBusinessUnit } = useOrganization();
  const { isRejected, isWithdrawn, isHired, isHidden, isActive } =
    getCandidateFlags(candidate);
  const {
    canManageJobPosts,
    canManageAffiliations,
    canManageProviderPools,
    canRevealProviders,
  } = permissions;

  return (
    <SheetSection title="Actions">
      <div className="grid gap-2 sm:grid-cols-2">
        {isHidden && canRevealProviders && (
          <RevealProviderButton
            providerUserId={candidate.userId}
            organizationBusinessUnitId={selectedOrgBusinessUnit.id}
            size="default"
            variant="outline"
          />
        )}
        {!isWithdrawn && (
          <ActionButton
            icon={UserCircle}
            onClick={() => actions.openProfile(candidate)}
          >
            View profile
          </ActionButton>
        )}
        {canManageProviderPools && isActive && !isHidden && (
          <ActionButton
            icon={UserPlus}
            onClick={() => actions.openAddToPool(candidate)}
          >
            Add to pool
          </ActionButton>
        )}
        {canManageAffiliations && isActive && !isHidden && isHired && (
          <ActionButton
            icon={UserX}
            onClick={() => actions.openMarkNotHired(candidate)}
          >
            Mark not hired
          </ActionButton>
        )}
        {canManageJobPosts && !isWithdrawn && isRejected && (
          <ActionButton
            icon={RotateCcw}
            onClick={() => actions.reinstateCandidate(candidate)}
            disabled={actions.setCandidateRejectionMutation.isPending}
          >
            Reinstate
          </ActionButton>
        )}
      </div>
    </SheetSection>
  );
}

export function CandidateDetailSheet({
  candidate,
  jobId,
  lapsed,
  permissions,
  currentUser,
  actions,
  onClose,
}: {
  candidate: Candidate | null;
  jobId: string;
  lapsed: LapsedRequirement[];
  permissions: CandidatePermissions;
  currentUser: CurrentUser | null;
  actions: JobDashboardActions;
  onClose: () => void;
}) {
  const { selectedOrgBusinessUnit } = useOrganization();

  return (
    <Sheet
      open={candidate !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        {candidate && (
          <CandidateDetailBody
            candidate={candidate}
            jobId={jobId}
            lapsed={lapsed}
            permissions={permissions}
            currentUser={currentUser}
            actions={actions}
            enableHiringProcess={!!selectedOrgBusinessUnit.enableHiringProcess}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

type CurrentUser = { id: string; isAdmin: boolean };

function CandidateDetailBody({
  candidate,
  jobId,
  lapsed,
  permissions,
  currentUser,
  actions,
  enableHiringProcess,
}: {
  candidate: Candidate;
  jobId: string;
  lapsed: LapsedRequirement[];
  permissions: CandidatePermissions;
  currentUser: CurrentUser | null;
  actions: JobDashboardActions;
  enableHiringProcess: boolean;
}) {
  const { selectedOrgBusinessUnit } = useOrganization();
  const flags = getCandidateFlags(candidate);
  const { isRejected, isWithdrawn, isHidden, isActive } = flags;
  const displayName = getCandidateDisplayName(candidate);
  const subtitle = formatCandidateSubtitle(candidate);
  const employers = getRecentEmployers(candidate);

  return (
    <div className="space-y-6">
      <SheetHeader className="space-y-1 pr-12 text-left">
        <div className="flex flex-wrap items-center gap-2">
          <SheetTitle className="text-xl [overflow-wrap:anywhere]">
            {displayName}
          </SheetTitle>
          <CandidateStatusBadge flags={flags} />
        </div>
        <SheetDescription asChild>
          <div className="space-y-1">
            {subtitle && <p>{subtitle}</p>}
            {candidate.interestedAt && (
              <p className="text-xs">
                Applied {format(new Date(candidate.interestedAt), "PPpp")}
              </p>
            )}
          </div>
        </SheetDescription>
      </SheetHeader>

      {enableHiringProcess && !isHidden && !isRejected && !isWithdrawn && (
        <HiringProcessInstanceProgress
          jobPostId={jobId}
          providerUserId={candidate.userId}
          canEditOrgItems={permissions.canManageJobPosts}
        />
      )}

      {permissions.canManageJobPosts && isActive && lapsed.length > 0 && (
        <SheetSection title="Requirements">
          <LapsedRequirementBadges lapsed={lapsed} />
        </SheetSection>
      )}

      {!isHidden && (
        <ProviderPools
          candidate={candidate}
          displayName={displayName}
          canRemove={permissions.canManageProviderPools}
          actions={actions}
        />
      )}

      {isRejected && candidate.rejectedReason && (
        <SheetSection title="Rejection reason">
          <p className="text-sm italic text-slate-600 [overflow-wrap:anywhere]">
            {candidate.rejectedReason}
          </p>
        </SheetSection>
      )}

      {isWithdrawn && <WithdrawalPanel candidate={candidate} />}

      {!isHidden && employers.length > 0 && (
        <SheetSection title="Recent employers">
          <p className="text-sm text-slate-700 [overflow-wrap:anywhere]">
            {employers.join(", ")}
          </p>
        </SheetSection>
      )}

      {permissions.canManageJobPosts && currentUser && (
        <CandidateNotesThread
          organizationBusinessUnitId={selectedOrgBusinessUnit.id}
          userId={candidate.userId}
          currentUserId={currentUser.id}
          isAdmin={currentUser.isAdmin}
        />
      )}

      <SheetActions
        candidate={candidate}
        permissions={permissions}
        actions={actions}
      />
    </div>
  );
}
